import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import './SafeClaw.css';

const SafeClaw = () => {
  const { language } = useLanguage();
  const t = getTranslation(language);
  const sc = t.safeclaw;
  const [detectedOS, setDetectedOS] = useState('unknown');

  useEffect(() => {
    window.scrollTo(0, 0);
    const platform = navigator.userAgent || navigator.platform || '';
    if (/Mac/i.test(platform)) {
      setDetectedOS('mac');
    } else if (/Win/i.test(platform)) {
      setDetectedOS('windows');
    } else if (/Linux/i.test(platform)) {
      setDetectedOS('linux');
    }
  }, []);

  const downloads = [
    {
      os: 'mac',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
      ),
      label: 'macOS',
      filename: 'SafeClaw-0.1.1-mac.dmg',
      href: '/contact?type=product'
    },
    {
      os: 'windows',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
          <path d="M3 12V6.5l8-1.1V12H3zm0 .5h8v6.6l-8-1.1V12.5zM11.5 5.3l9.5-1.3v8h-9.5V5.3zM21 12.5v7.5l-9.5-1.3V12.5H21z"/>
        </svg>
      ),
      label: 'Windows',
      filename: 'SafeClaw-0.1.1-win.exe',
      href: '/contact?type=product'
    },
    {
      os: 'linux',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
          <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.368 1.884 1.43.868.134 1.703-.272 2.191-.574.3-.18.599-.135.834-.268.249-.135.267-.27.332-.534.098-.267.135-.4.098-.665a3.267 3.267 0 00-.333-1.354c-.133-.198-.198-.135-.265-.268 0-.199.065-.2.065-.399 0-.2-.132-.269-.332-.534a3.212 3.212 0 00-.733-.869c.061-.534.029-1.063-.136-1.564-.398-1.182-1.424-1.933-2.466-2.539a6.34 6.34 0 01-.332-.199c-.39-.272-.6-.571-.533-1.003.066-.399.266-.861.4-1.395.134-.601.2-1.304-.068-2.07-.267-.799-.664-1.099-1.2-1.533-.535-.399-1.267-.6-1.533-.932-.2-.267-.132-.6-.132-1 0-.4-.065-.866-.065-1.333 0-.5.132-.866.132-1.399 0-.2-.198-.533-.465-.666-.267-.133-.665-.267-.931-.267z"/>
        </svg>
      ),
      label: 'Linux',
      filename: 'SafeClaw-0.1.1-linux.AppImage',
      href: '/contact?type=product'
    }
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-50px" },
    transition: { duration: 0.6 }
  };



  return (
    <div className="safeclaw-page">
      {/* Hero Section */}
      <section className="sc-hero">
        <div className="sc-hero-bg">
          <div className="sc-hero-glow" />
          <div className="sc-hero-grid" />
        </div>
        <div className="sc-hero-container">
          <motion.div
            className="sc-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="sc-hero-badge">
              <span className="sc-badge-dot" />
              {sc.hero.badge}
            </div>
            <div className="sc-hero-title-row">
              <img 
                src="/safeclaw_logo_trimmed.png"
                alt="SafeClaw Logo" 
                className="sc-hero-logo"
              />
              <h1 className="sc-hero-title">
                Safe<span className="sc-highlight">Claw</span>
              </h1>
            </div>
            <p className="sc-hero-subtitle">{sc.hero.subtitle}</p>
            <p className="sc-hero-desc">{sc.hero.description}</p>
            <div className="sc-hero-cta">
              <a href="#sc-download" className="sc-btn sc-btn-primary">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                </svg>
                {sc.hero.download}
              </a>
              <a href="#sc-features" className="sc-btn sc-btn-ghost">
                {sc.hero.learnMore}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path d="M19 9l-7 7-7-7"/>
                </svg>
              </a>
            </div>
            <div className="sc-hero-meta">
              <span className="sc-meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg>
                {sc.hero.metaOpen}
              </span>
              <span className="sc-meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                {sc.hero.metaSafe}
              </span>
              <span className="sc-meta-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                {sc.hero.metaCross}
              </span>
            </div>
          </motion.div>
          <motion.div
            className="sc-hero-visual"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="sc-terminal">
              <div className="sc-terminal-header">
                <span className="sc-dot sc-dot-red" />
                <span className="sc-dot sc-dot-yellow" />
                <span className="sc-dot sc-dot-green" />
                <span className="sc-terminal-title">SafeClaw Terminal</span>
              </div>
              <div className="sc-terminal-body">
                <div className="sc-terminal-line">
                  <span className="sc-prompt">$</span>
                  <span className="sc-command">safeclaw init --security high</span>
                </div>
                <div className="sc-terminal-line sc-output">
                  <span className="sc-success">✓</span> {sc.hero.terminalInit}
                </div>
                <div className="sc-terminal-line sc-output">
                  <span className="sc-success">✓</span> {sc.hero.terminalSandbox}
                </div>
                <div className="sc-terminal-line sc-output">
                  <span className="sc-success">✓</span> {sc.hero.terminalReady}
                </div>
                <div className="sc-terminal-line">
                  <span className="sc-prompt">$</span>
                  <span className="sc-command">safeclaw status</span>
                </div>
                <div className="sc-terminal-line sc-output sc-status-line">
                  <span className="sc-status-label">{sc.hero.terminalStatus}</span>
                  <span className="sc-status-active">● {sc.hero.terminalRunning}</span>
                </div>
                <div className="sc-terminal-line sc-output sc-status-line">
                  <span className="sc-status-label">{sc.hero.terminalSecurity}</span>
                  <span className="sc-status-secure">🔒 {sc.hero.terminalMax}</span>
                </div>
                <div className="sc-terminal-line">
                  <span className="sc-prompt">$</span>
                  <span className="sc-cursor">|</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Advantages */}
      <section className="sc-features" id="sc-features">
        <div className="sc-section-container">
          <motion.div className="sc-section-header" {...fadeInUp}>
            <h2 className="sc-section-title">{sc.features.title}</h2>
            <p className="sc-section-subtitle">{sc.features.subtitle}</p>
          </motion.div>
          <div className="sc-features-grid">
            {sc.features.items.map((item, index) => (
              <motion.div
                key={index}
                className="sc-feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="sc-feature-icon">{item.icon}</div>
                <h3 className="sc-feature-title">{item.title}</h3>
                <p className="sc-feature-desc">{item.description}</p>
                <ul className="sc-feature-list">
                  {item.points.map((point, i) => (
                    <li key={i}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M22 4L12 14.01l-3-3"/><path d="M22 4L12 14.01"/></svg>
                      {point}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="sc-usecases">
        <div className="sc-section-container">
          <motion.div className="sc-section-header" {...fadeInUp}>
            <h2 className="sc-section-title">{sc.usecases.title}</h2>
            <p className="sc-section-subtitle">{sc.usecases.subtitle}</p>
          </motion.div>
          <div className="sc-usecases-grid">
            {sc.usecases.categories.map((cat, index) => (
              <motion.div
                key={index}
                className="sc-usecase-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <div className="sc-usecase-emoji">{cat.emoji}</div>
                <h3 className="sc-usecase-title">{cat.title}</h3>
                <ul className="sc-usecase-list">
                  {cat.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="sc-comparison">
        <div className="sc-section-container">
          <motion.div className="sc-section-header" {...fadeInUp}>
            <h2 className="sc-section-title">{sc.comparison.title}</h2>
            <p className="sc-section-subtitle">{sc.comparison.subtitle}</p>
          </motion.div>
          <motion.div
            className="sc-comparison-table-wrapper"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <table className="sc-comparison-table">
              <thead>
                <tr>
                  <th>{sc.comparison.header.feature}</th>
                  <th className="sc-col-highlight">
                    <div className="sc-col-badge">★</div>
                    SafeClaw
                  </th>
                  <th>OpenClaw</th>
                </tr>
              </thead>
              <tbody>
                {sc.comparison.rows.map((row, index) => (
                  <tr key={index}>
                    <td className="sc-feature-name">{row.feature}</td>
                    <td className="sc-col-highlight">
                      <span className="sc-check">✓</span> {row.safeclaw}
                    </td>
                    <td>
                      <span className={row.openclawStatus === 'partial' ? 'sc-partial' : 'sc-cross'}>
                        {row.openclawStatus === 'partial' ? '~' : '✗'}
                      </span>
                      {' '}{row.openclaw}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="sc-tech">
        <div className="sc-section-container">
          <motion.div className="sc-section-header" {...fadeInUp}>
            <h2 className="sc-section-title">{sc.tech.title}</h2>
            <p className="sc-section-subtitle">{sc.tech.subtitle}</p>
          </motion.div>
          <div className="sc-tech-grid">
            {sc.tech.items.map((item, index) => (
              <motion.div
                key={index}
                className="sc-tech-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="sc-tech-icon">{item.icon}</div>
                <h3 className="sc-tech-title">{item.title}</h3>
                <ul className="sc-tech-list">
                  {item.points.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="sc-pricing" id="sc-pricing">
        <div className="sc-section-container">
          <motion.div className="sc-section-header" {...fadeInUp}>
            <h2 className="sc-section-title">{sc.pricing.title}</h2>
            <p className="sc-section-subtitle">{sc.pricing.subtitle}</p>
          </motion.div>
          <div className="sc-pricing-grid">
            {sc.pricing.plans.map((plan, index) => (
              <motion.div
                key={index}
                className={`sc-pricing-card ${plan.featured ? 'sc-pricing-featured' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
              >
                {plan.featured && (
                  <div className="sc-pricing-popular">{sc.pricing.popular}</div>
                )}
                <div className="sc-pricing-header">
                  <h3 className="sc-pricing-name">{plan.name}</h3>
                  <div className="sc-pricing-price">
                    {plan.originalPrice && (
                      <span className="sc-pricing-original">{plan.originalPrice}</span>
                    )}
                    <span className="sc-pricing-amount">{plan.price}</span>
                    <span className="sc-pricing-period">{plan.period}</span>
                  </div>
                </div>
                <p className="sc-pricing-desc">{plan.description}</p>
                <ul className="sc-pricing-features">
                  {plan.features.map((feat, i) => (
                    <li key={i}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><path d="M20 6L9 17l-5-5"/></svg>
                      {feat}
                    </li>
                  ))}
                </ul>
                <a href="/contact?type=product" className={`sc-pricing-btn ${plan.featured ? 'sc-pricing-btn-primary' : ''}`}>
                  {plan.cta}
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="sc-download" id="sc-download">
        <div className="sc-section-container">
          <motion.div className="sc-section-header" {...fadeInUp}>
            <h2 className="sc-section-title">{sc.download.title}</h2>
            <p className="sc-section-subtitle">{sc.download.subtitle}</p>
          </motion.div>
          <div className="sc-download-version">
            <span className="sc-version-badge">v0.1.1</span>
            <span className="sc-version-date">{sc.download.releaseDate}</span>
          </div>
          <div className="sc-download-grid">
            {downloads.map((dl, index) => (
              <motion.a
                key={dl.os}
                href={dl.href}
                className={`sc-download-card ${detectedOS === dl.os ? 'sc-recommended' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -4 }}
              >
                {detectedOS === dl.os && (
                  <div className="sc-recommended-badge">{sc.download.recommended}</div>
                )}
                <div className="sc-download-icon">{dl.icon}</div>
                <div className="sc-download-label">{dl.label}</div>
                <div className="sc-download-filename">{dl.filename}</div>
                <div className="sc-download-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                  </svg>
                  {language === 'zh' ? '咨询下载' : 'Enquire about download'}
                </div>
              </motion.a>
            ))}
          </div>
          <motion.div className="sc-download-info" {...fadeInUp}>
            <div className="sc-requirements">
              <h4>{sc.download.requirements.title}</h4>
              <ul>
                {sc.download.requirements.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SafeClaw;
